import React from 'react';
import {useTranslation} from 'react-i18next';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import SectionCard from './SectionCard';
import SettingRow from './SettingRow';
import StatusPill from './StatusPill';
import settingsStyles from '../settingsStyles';
import {formatDateRange} from '../inventoryGaps';

/**
 * Channex kanal yoneticisi baglantisi.
 *
 * Kullanicidan girdi alinmaz: tesis bilgileri panelde zaten kayitli oldugu icin
 * baglanti tek tikla kurulur. Servis tarafinda Channex'te property olusturulup
 * channel_manager_property_mapping tablosuna eslesme yazilir.
 *
 * status: {
 *   connected, channel_manager_property_id, room_count, last_sync_at,
 *   inventory: {           // opsiyonel — servis dondurmezse bolum hic cizilmez
 *     synced_rooms, total_rooms,
 *     last_push_at, pending_count, failed_count,
 *     date_range: { from, to }
 *   }
 * }
 *
 * Kontenjan ozeti opsiyonel alanlardan olusur: her satir yalnizca ilgili alan
 * geldiginde gosterilir, boylece servis sekli netlestikce arayuz kirilmaz.
 */
export default function ChannexCard({status, fetching, connecting, onConnect, notReady}) {
    const {t} = useTranslation();
    const classes = settingsStyles();

    const connected = !!(status && status.connected);
    const channexPropertyId = status && status.channel_manager_property_id;
    const inventory = (status && status.inventory) || null;

    const hasNumber = (value) => value !== null && value !== undefined && value !== '';
    const failedCount = inventory && Number(inventory.failed_count) > 0 ? Number(inventory.failed_count) : 0;
    const pendingCount = inventory && Number(inventory.pending_count) > 0 ? Number(inventory.pending_count) : 0;

    const handleConnect = () => {
        if (typeof onConnect === 'function') return onConnect();
        return notReady && notReady();
    };

    const description = connected
        ? t('enw-web-settings-channex_connected_desc', {
            defaultValue: 'Tesis Channex ile eşleşti. Kontenjan ve fiyat güncellemeleri kanallara otomatik iletiliyor.{{rooms}}',
            rooms: status && status.room_count
                ? ` (${status.room_count} oda eşleşmesi)`
                : '',
        })
        : t('enw-web-settings-channex_desc', 'Kontenjan, fiyat ve rezervasyonlarınızın kanallarla senkron çalışması için tesisinizi Channex’e bağlayın.');

    return (
        <SectionCard
            id="baglantilar"
            icon="fas fa-plug"
            title={t('enw-web-settings-connections_title', 'Bağlantılar')}
            description={t('enw-web-settings-connections_desc', 'Kanal yönetimi ve senkronizasyon servisleri')}
        >
            {fetching ? (
                <div className={classes.loadingBox}>
                    <CircularProgress size={28} style={{color: '#d71f26'}} />
                </div>
            ) : (
                <SettingRow
                    icon="fas fa-random"
                    title="Channex"
                    badge={connected
                        ? <StatusPill tone="ok" label={t('enw-web-settings-connected', 'Bağlı')} />
                        : <StatusPill tone="off" label={t('enw-web-settings-channex_not_connected', 'Bağlı değil')} />}
                    active={connected}
                    description={description}
                    control={
                        <Button
                            className={`${classes.btn} ${connected ? '' : classes.btnPrimary}`}
                            onClick={handleConnect}
                            disabled={connecting}
                            startIcon={connecting
                                ? <CircularProgress size={14} style={{color: connected ? '#8492a6' : '#fff'}} />
                                : <i className={connected ? 'fas fa-sync-alt' : 'fas fa-link'} />}
                        >
                            {connecting
                                ? t('enw-web-settings-channex_connecting', 'Bağlanıyor…')
                                : connected
                                    ? t('enw-web-settings-channex_resync', 'Yeniden Eşleştir')
                                    : t('enw-web-settings-channex_connect', 'Channex’e Bağla')}
                        </Button>
                    }
                >
                    {connected ? (
                        <>
                            <div className={classes.subGrid}>
                                {channexPropertyId && (
                                    <div>
                                        <span className={classes.metaLabel}>
                                            {t('enw-web-settings-channex_property_id', 'Channex tesis kimliği')}
                                        </span>
                                        <span className={classes.metaValue}>{channexPropertyId}</span>
                                    </div>
                                )}
                                {status && status.last_sync_at && (
                                    <div>
                                        <span className={classes.metaLabel}>
                                            {t('enw-web-settings-channex_last_sync', 'Son senkron')}
                                        </span>
                                        <span className={classes.metaValue}>{status.last_sync_at}</span>
                                    </div>
                                )}
                            </div>
                            {inventory && (
                                <div className={classes.channexInventory}>
                                    <div className={classes.channexInventoryHead}>
                                        <i className="fas fa-calendar-check" />
                                        {t('enw-web-settings-channex_inventory', 'Kontenjan senkronu')}
                                        {(failedCount > 0 || pendingCount > 0) && (
                                            <span className={classes.channexInventoryPills}>
                                                {pendingCount > 0 && (
                                                    <StatusPill
                                                        tone="warn"
                                                        label={t('enw-web-settings-channex_pending', {
                                                            defaultValue: '{{count}} bekliyor',
                                                            count: pendingCount,
                                                        })}
                                                    />
                                                )}
                                                {failedCount > 0 && (
                                                    <StatusPill
                                                        tone="warn"
                                                        label={t('enw-web-settings-channex_failed_count', {
                                                            defaultValue: '{{count}} başarısız',
                                                            count: failedCount,
                                                        })}
                                                    />
                                                )}
                                            </span>
                                        )}
                                    </div>

                                    <div className={classes.subGrid}>
                                        {hasNumber(inventory.synced_rooms) && (
                                            <div>
                                                <span className={classes.metaLabel}>
                                                    {t('enw-web-settings-channex_synced_rooms', 'Eşleşen oda')}
                                                </span>
                                                <span className={classes.metaValue}>
                                                    {hasNumber(inventory.total_rooms)
                                                        ? `${inventory.synced_rooms} / ${inventory.total_rooms}`
                                                        : inventory.synced_rooms}
                                                </span>
                                            </div>
                                        )}
                                        {inventory.last_push_at && (
                                            <div>
                                                <span className={classes.metaLabel}>
                                                    {t('enw-web-settings-channex_last_push', 'Son kontenjan gönderimi')}
                                                </span>
                                                <span className={classes.metaValue}>{inventory.last_push_at}</span>
                                            </div>
                                        )}
                                        {inventory.date_range && inventory.date_range.from && (
                                            <div>
                                                <span className={classes.metaLabel}>
                                                    {t('enw-web-settings-channex_date_range', 'Gönderilen dönem')}
                                                </span>
                                                <span className={classes.metaValue}>
                                                    {formatDateRange(inventory.date_range.from, inventory.date_range.to)}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {failedCount > 0 && (
                                        <p className={classes.hint}>
                                            {t('enw-web-settings-channex_failed_hint', 'Başarısız gönderimler bir sonraki senkronda yeniden denenir. Sürerse oda ve fiyat eşleşmelerinizi kontrol edin.')}
                                        </p>
                                    )}
                                </div>
                            )}

                            <p className={classes.hint}>
                                {t('enw-web-settings-channex_resync_hint', 'Yeniden eşleştirme oda ve fiyat tanımlarınızı Channex ile karşılaştırır; mevcut kanal bağlantılarınız korunur.')}
                            </p>
                        </>
                    ) : (
                        <p className={classes.hint}>
                            {t('enw-web-settings-channex_connect_hint', 'Bağlantı için ek bilgi girmenize gerek yok; tesis, oda ve fiyat tanımlarınız panelden aktarılır. İşlem birkaç dakika sürebilir.')}
                        </p>
                    )}
                </SettingRow>
            )}
        </SectionCard>
    );
}
